import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronDown, IconX, IconSearch, IconCheck } from '@tabler/icons-react';
import type { DeliverObjectDto, DeliverObjectDependencyDto, PhaseDto } from '../../api/model';
import { DependencyType } from '../../api/model';
import { useCreateDependency, useUpdateDependency } from '../../hooks/useDeliverableDependencies';
import { useToast } from '../ui/Toast/ToastProvider';
import { getApiErrorMessage } from '../../hooks/useApiMutation';
import {getPhaseLabel, isAgileArtefactDeliverable} from '../../utils/phaseUtils';

import styles from './DependencyModal.module.scss';

interface DependencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliverables: DeliverObjectDto[];
  phases?: PhaseDto[];
  existing?: DeliverObjectDependencyDto | null;
  defaultPredecessorId?: number;
  defaultSuccessorId?: number;
  lockPredecessor?: boolean;
  lockSuccessor?: boolean;
  readOnlyView?: boolean;
  hideDeleteButton?: boolean;
  onSaved?: () => void;
  onDelete?: () => void;
  onUpdate?: (data: DeliverObjectDependencyDto) => void;
}

const UNASSIGNED_KEY = 'unassigned';

const deliverableLabel = (d: DeliverObjectDto, deliverableFallback: string): string =>
  (d.name ? d.name.replaceAll('_', ' ') : `${deliverableFallback} ${d.id}`);

const formatShortDate = (date?: string): string | undefined => {
  if (!date) return undefined;
  const parsed = new Date(date.toString());
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  });
};

interface DeliverableGroup {
  phaseKey: string;
  phaseLabel: string;
  items: DeliverObjectDto[];
}

interface DeliverablePickerProps {
  label: string;
  required?: boolean;
  value?: number;
  groups: DeliverableGroup[];
  phaseLabelById: Map<number, string>;
  placeholder: string;
  disabled?: boolean;
  onChange: (id?: number) => void;
  deliverableFallback: string;
}

const DeliverablePicker: React.FC<DeliverablePickerProps> = ({
  label,
  required,
  value,
  groups,
  phaseLabelById,
  placeholder,
  disabled,
  onChange,
  deliverableFallback,
}) => {
  const { t } = useTranslation('gantt');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const selected = useMemo(() => {
    if (value == null) return undefined;
    for (const group of groups) {
      const found = group.items.find(d => d.id === value);
      if (found) return found;
    }
    return undefined;
  }, [value, groups]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(d => {
          const name = deliverableLabel(d, deliverableFallback).toLowerCase();
          const phase = group.phaseLabel.toLowerCase();
          return name.includes(term) || phase.includes(term);
        }),
      }))
      .filter(group => group.items.length > 0);
  }, [groups, search, deliverableFallback]);

  const selectedPhaseLabel =
    selected?.phaseId != null ? phaseLabelById.get(selected.phaseId) : undefined;
  const selectedDate = formatShortDate(selected?.startDate);

  return (
    <div className={styles.inputGroup} ref={containerRef}>
      <label className={`${styles.inputLabel} ${required ? styles.required : ''}`}>{label}</label>
      <div className={styles.pickerWrapper}>
        <button
          type="button"
          className={styles.pickerButton}
          disabled={disabled}
          onClick={() => !disabled && setOpen(o => !o)}
        >
          {selected ? (
            <span className={styles.pickerValue}>
              <span className={styles.pickerValueName}>{deliverableLabel(selected, deliverableFallback)}</span>
              <span className={styles.pickerValueMeta}>
                {selectedPhaseLabel ?? t('dependency.modal.noPhase')}
                {selectedDate ? ` · ${selectedDate}` : ''}
              </span>
            </span>
          ) : (
            <span className={styles.pickerPlaceholder}>{placeholder}</span>
          )}
          <IconChevronDown size={18} className={styles.pickerChevron} />
        </button>

        {open && (
          <div className={styles.pickerDropdown}>
            <div className={styles.pickerSearch}>
              <IconSearch size={16} />
              <input
                autoFocus
                type="text"
                value={search}
                placeholder={t('dependency.modal.filterDeliverables')}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.pickerList}>
              {filteredGroups.length === 0 ? (
                <div className={styles.pickerEmpty}>{t('dependency.modal.noDeliverables')}</div>
              ) : (
                filteredGroups.map(group => (
                  <div className={styles.pickerGroup} key={group.phaseKey}>
                    <div className={styles.pickerGroupLabel}>{group.phaseLabel}</div>
                    {group.items.map(d => {
                      const date = formatShortDate(d.startDate);
                      const isSelected = d.id === value;
                      return (
                        <button
                          type="button"
                          key={d.id}
                          className={`${styles.pickerOption} ${isSelected ? styles.pickerOptionSelected : ''}`}
                          onClick={() => {
                            onChange(d.id);
                            setOpen(false);
                          }}
                        >
                          <span className={styles.pickerOptionName}>{deliverableLabel(d, deliverableFallback)}</span>
                          <span className={styles.pickerOptionMeta}>
                            {date ?? t('dependency.modal.noStartDate')}
                          </span>
                          {isSelected && <IconCheck size={16} className={styles.pickerOptionCheck} />}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DependencyModal: React.FC<DependencyModalProps> = ({
  isOpen,
  onClose,
  deliverables,
  phases = [],
  existing,
  defaultPredecessorId,
  defaultSuccessorId,
  lockPredecessor,
  lockSuccessor,
  readOnlyView,
  hideDeleteButton,
  onSaved,
  onDelete,
  onUpdate,
}) => {
  const { showSuccess, showError } = useToast();
  const { t } = useTranslation('gantt');
  const createMutation = useCreateDependency();
  const updateMutation = useUpdateDependency();

  const isEditing = !!existing?.id;

  const [predecessorId, setPredecessorId] = useState<number | undefined>(undefined);
  const [successorId, setSuccessorId] = useState<number | undefined>(undefined);
  const [type, setType] = useState<string>(DependencyType.MUST_FINISH_FIRST);
  const [offsetDays, setOffsetDays] = useState<number>(0);
  const [critical, setCritical] = useState<boolean>(false);
  const [showAgileArtefacts, setShowAgileArtefacts] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialise form whenever the modal opens or the target changes.
  useEffect(() => {
    if (!isOpen) return;
    setPredecessorId(existing?.predecessorId ?? defaultPredecessorId);
    setSuccessorId(existing?.successorId ?? defaultSuccessorId);
    setType(existing?.type ?? DependencyType.MUST_FINISH_FIRST);
    setOffsetDays(existing?.offsetDays ?? 0);
    setCritical(existing?.critical ?? false);
    setShowAgileArtefacts(false);
    setError(null);
  }, [isOpen, existing, defaultPredecessorId, defaultSuccessorId]);

  const phaseLabelById = useMemo(() => {
    const map = new Map<number, string>();
    phases.forEach(p => {
      if (p.id != null) map.set(p.id, getPhaseLabel(p, phases));
    });
    return map;
  }, [phases]);

  // Phase ordering by start date.
  const phaseOrderById = useMemo(() => {
    const ordered = [...phases].sort((a, b) => {
      const aTime = a.startTime ? new Date(a.startTime.toString()).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.startTime ? new Date(b.startTime.toString()).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
    const map = new Map<number, number>();
    ordered.forEach((p, index) => {
      if (p.id != null) map.set(p.id, index);
    });
    return map;
  }, [phases]);

  // Build grouped + sorted deliverable options
  const groups = useMemo<DeliverableGroup[]>(() => {
    const visible = deliverables.filter(d => {
      if (showAgileArtefacts) return true;
      if (d.id === predecessorId || d.id === successorId) return true;
        return !isAgileArtefactDeliverable(d);
    });

    const byPhase = new Map<string, DeliverObjectDto[]>();
    visible.forEach(d => {
      const key = d.phaseId != null ? `phase-${d.phaseId}` : UNASSIGNED_KEY;
      if (!byPhase.has(key)) byPhase.set(key, []);
      byPhase.get(key)!.push(d);
    });

    const result: DeliverableGroup[] = [];
    byPhase.forEach((items, key) => {
      items.sort((a, b) => {
        const aTime = a.startDate ? new Date(a.startDate.toString()).getTime() : Number.POSITIVE_INFINITY;
        const bTime = b.startDate ? new Date(b.startDate.toString()).getTime() : Number.POSITIVE_INFINITY;
        if (aTime !== bTime) return aTime - bTime;
        return deliverableLabel(a, t('dependency.modal.deliverableFallback')).localeCompare(deliverableLabel(b, t('dependency.modal.deliverableFallback')));
      });
      const phaseId = key === UNASSIGNED_KEY ? undefined : Number(key.replace('phase-', ''));
      const phaseLabel =
        phaseId != null ? phaseLabelById.get(phaseId) ?? `Phase ${phaseId}` : t('dependency.modal.noPhase');
      result.push({ phaseKey: key, phaseLabel, items });
    });

    result.sort((a, b) => {
      if (a.phaseKey === UNASSIGNED_KEY) return 1;
      if (b.phaseKey === UNASSIGNED_KEY) return -1;
      const aId = Number(a.phaseKey.replace('phase-', ''));
      const bId = Number(b.phaseKey.replace('phase-', ''));
      const aOrder = phaseOrderById.get(aId) ?? Number.POSITIVE_INFINITY;
      const bOrder = phaseOrderById.get(bId) ?? Number.POSITIVE_INFINITY;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.phaseLabel.localeCompare(b.phaseLabel);
    });

    return result;
  }, [deliverables, showAgileArtefacts, predecessorId, successorId, phaseLabelById, phaseOrderById]);

  const hasHiddenAgileArtefacts = useMemo(
      () => deliverables.some(isAgileArtefactDeliverable),
    [deliverables]
  );

  if (!isOpen) return null;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const validate = (): string | null => {
    if (!predecessorId) return t('dependency.validation.predecessorRequired');
    if (!successorId) return t('dependency.validation.successorRequired');
    if (predecessorId === successorId) {
      return t('dependency.validation.selfDependency');
    }
    if (!Number.isInteger(offsetDays)) {
      return t('dependency.validation.offsetInteger');
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    const dto: DeliverObjectDependencyDto = {
      predecessorId,
      successorId,
      type: type as DeliverObjectDependencyDto['type'],
      offsetDays,
      critical,
    };

    try {
      if (isEditing && existing?.id != null) {
        if (onUpdate) {
          onUpdate(dto);
        } else {
          await updateMutation.mutateAsync({ id: existing.id, data: dto });
          showSuccess(t('dependency.toast.updated'));
          onSaved?.();
          onClose();
        }
      } else {
        await createMutation.mutateAsync({ data: dto });
        showSuccess(t('dependency.toast.created'));
        onSaved?.();
        onClose();
      }
    } catch (e) {
      showError(t('dependency.toast.saveError'), getApiErrorMessage(e));
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.popup} onClick={e => e.stopPropagation()}>
        <div className={styles.popupHeader}>
          <div className={styles.headerContent}>
            <h2 className={styles.headerTitle}>
              {readOnlyView ? t('dependency.viewDependency') : (isEditing ? t('dependency.editDependency') : t('dependency.addDependency'))}
            </h2>
            {!readOnlyView && (
              <p className={styles.headerDescription}>
                {t('dependency.modal.description')}
              </p>
            )}
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label={t('dependency.ariaClose')}>
            <IconX size={20} />
          </button>
        </div>

        <div className={styles.formBody}>
          {readOnlyView ? (
            <div className={styles.infoView}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('dependency.modal.predecessor')}:</span>
                <span className={styles.infoValue}>
                  {deliverableLabel(
                    deliverables.find(d => d.id === predecessorId) || {},
                    t('dependency.modal.deliverableFallback')
                  )}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('dependency.modal.successor')}:</span>
                <span className={styles.infoValue}>
                  {deliverableLabel(
                    deliverables.find(d => d.id === successorId) || {},
                    t('dependency.modal.deliverableFallback')
                  )}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('dependency.modal.typeLabel')}:</span>
                <span className={styles.infoValue}>{t(`dependency.types.${type}`)}</span>
              </div>
              {offsetDays !== 0 && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{t('dependency.modal.offsetLabel')}:</span>
                  <span className={styles.infoValue}>{offsetDays} days</span>
                </div>
              )}
              {critical && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{t('dependency.modal.markCritical')}:</span>
                  <span className={styles.infoValue}>{t('dependency.modal.yes')}</span>
                </div>
              )}
            </div>
          ) : (
            <>
              {hasHiddenAgileArtefacts && (
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={showAgileArtefacts}
                    onChange={e => setShowAgileArtefacts(e.target.checked)}
                  />
                  <span>{t('dependency.modal.showAgileArtefacts')}</span>
                </label>
              )}

              <DeliverablePicker
                label={t('dependency.modal.predecessor')}
                required
                value={predecessorId}
                groups={groups}
                phaseLabelById={phaseLabelById}
                placeholder={t('dependency.modal.selectPredecessor')}
                disabled={lockPredecessor}
                onChange={setPredecessorId}
                deliverableFallback={t('dependency.modal.deliverableFallback')}
              />

              <DeliverablePicker
                label={t('dependency.modal.successor')}
                required
                value={successorId}
                groups={groups}
                phaseLabelById={phaseLabelById}
                placeholder={t('dependency.modal.selectSuccessor')}
                disabled={lockSuccessor}
                onChange={setSuccessorId}
                deliverableFallback={t('dependency.modal.deliverableFallback')}
              />

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>{t('dependency.modal.typeLabel')}</label>
                <div className={styles.selectField}>
                  <select
                    className={styles.select}
                    value={type}
                    onChange={e => setType(e.target.value)}
                  >
                    {Object.entries(DependencyType).map(([ , value]) => (
                    <option key={value} value={value}>
                      {t(`dependency.types.${value}`)}
                    </option>
                  ))}
                  </select>
                  <IconChevronDown />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>{t('dependency.modal.offsetLabel')}</label>
                <input
                  type="number"
                  className={styles.input}
                  value={Number.isNaN(offsetDays) ? '' : offsetDays}
                  step={1}
                  onChange={e => setOffsetDays(parseInt(e.target.value, 10))}
                  placeholder="0"
                />
                <span className={styles.hint}>
                  {t('dependency.modal.offsetHint')}
                </span>
              </div>

              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={critical}
                  onChange={e => setCritical(e.target.checked)}
                />
                <span>{t('dependency.modal.markCritical')}</span>
              </label>

              {error && <div className={styles.errorText}>{error}</div>}
            </>
          )}
        </div>

        <div className={styles.footer}>
          {isEditing && onDelete && !hideDeleteButton && (
            <button 
              className={styles.deleteButton} 
              onClick={() => {
                onDelete();
              }} 
              disabled={isSaving}
              type="button"
            >
              {t('dependency.modal.delete')}
            </button>
          )}
          <div className={styles.footerRight}>
            <button className={styles.cancelButton} onClick={onClose} disabled={isSaving}>
              {readOnlyView ? t('dependency.modal.close') : t('dependency.modal.cancel')}
            </button>
            {!readOnlyView && (
              <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? t('dependency.modal.saving') : isEditing ? t('dependency.modal.save') : t('dependency.addDependency')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DependencyModal;
